import { useCallback } from 'react';
import { Button, Card, Form, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { useResource } from 'app/common/api';
import {
  NotificationsRender,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { CanAccess } from 'app/railways/domains/access/features/permissions';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { appRoute } from 'app/terminals/router/routes';

import { Loading } from '../../../../ui';
import { PageContent } from '../../../../ui/page-content';
import { TEmployee } from '../core/employee';
import { EmployeeRoleList } from '../features/roles/role-list';
import { addEmployeeRequest } from '../requests';
import { EmployeeFields } from './employee-fields';

type NewEmployee = Omit<TEmployee, 'id'>;

export const AddEmployeePage = observer(() => {
  const navigate = useNavigate();
  const authStatusStore = useAuthStatusWriter();
  const notify = useNotifyFactory();
  const addEmployeeResource = useResource<NewEmployee, TEmployee>({
    request: addEmployeeRequest,
    errorNotify: notify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: ({ id }) => {
      const newEmployeePath = appRoute.EMPLOYEE.buildPath({ id });
      navigate(newEmployeePath);
    },
  });

  const isLoading = addEmployeeResource.isLoading;

  const handleFormSubmit = useCallback(
    (newEmployee: Omit<TEmployee, 'id'>) => {
      addEmployeeResource.send(newEmployee);
    },
    [addEmployeeResource],
  );

  return (
    <PageContent>
      <CanAccess
        perm="PERM_EMPLOYEE_CREATE"
        fallback="У вас нет разрешений для просмотра этой страницы"
      >
        <Form
          layout="vertical"
          onFinish={handleFormSubmit}
          scrollToFirstError={{ behavior: 'smooth' }}
        >
          <Loading active={isLoading}>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: 'flex' }}
            >
              <Card size="small" title="Данные входа">
                <EmployeeFields />
              </Card>

              <Card size="small" title="Роли и разрешения">
                <p>
                  Выберите необходимое роли, которые будут назначены сотруднику
                </p>
                <Form.Item
                  name="roles"
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите необходимые роли',
                    },
                  ]}
                >
                  <EmployeeRoleList />
                </Form.Item>
              </Card>

              <NotificationsRender notifyStore={notify} />

              <Card size="small">
                <div style={{ display: 'flex' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ marginLeft: 'auto' }}
                  >
                    Добавить пользователя
                  </Button>
                </div>
              </Card>
            </Space>
          </Loading>
        </Form>
      </CanAccess>
    </PageContent>
  );
});
